import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur } from "@/lib/format";
import { toast } from "sonner";
import { Building2, LogIn, Search, Sparkles } from "lucide-react";

export default function ImpersonateUsers() {
  const navigate = useNavigate();
  const { loadMe } = useAuth();
  const [orgs, setOrgs] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/admin/organizations").then(({ data }) => setOrgs(data)).catch(() => setOrgs([]));
  }, []);

  const enter = async (o) => {
    setBusy(o.id);
    try {
      const { data } = await api.post(`/admin/impersonate/${o.id}`);
      if (!data.self) await loadMe();
      toast.success(data.self ? `${data.org_name} è la tua azienda: la usi direttamente` : `Stai impersonando ${data.org_name}`);
      navigate("/home");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile impersonare questa azienda");
      setBusy(null);
    }
  };

  const demo = (orgs || []).find((o) => /demo/i.test(o.name || "") || /demo/i.test(o.contacts?.email || ""));
  const filtered = (orgs || []).filter(
    (o) => o.name.toLowerCase().includes(q.toLowerCase()) || (o.contacts?.email || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="impersonate-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Impersona Utente</h1>
        <p className="text-muted-foreground mt-1 text-sm">Entra nella vista di un'azienda registrata come se fossi il proprietario. In alto comparirà un banner per tornare alla console admin.</p>
      </div>

      {demo && (
        <div className="bg-card border-2 border-[#E3FF00] rounded-xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid="impersonate-demo-card">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg grid place-items-center bg-[#E3FF00]"><Sparkles className="h-5 w-5 text-neutral-900" /></div>
            <div>
              <div className="font-display font-semibold">Azienda Demo</div>
              <div className="text-xs text-muted-foreground">{demo.name} · vista pronta per dimostrazioni e test</div>
            </div>
          </div>
          <button onClick={() => enter(demo)} disabled={busy === demo.id} className="bg-[#E3FF00] text-neutral-900 font-semibold px-4 py-2.5 rounded-lg hover:brightness-95 flex items-center gap-2 disabled:opacity-60" data-testid="impersonate-demo-button">
            <LogIn className="h-4 w-4" /> Entra come azienda demo
          </button>
        </div>
      )}

      <div className="relative max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca azienda o email..." className="w-full bg-background border border-input rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" data-testid="impersonate-search" />
      </div>

      <div className="bg-card rounded-xl border border-border/80 overflow-x-auto" data-testid="impersonate-orgs-table">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
            <th className="px-4 py-3 font-semibold">Azienda</th>
            <th className="px-4 py-3 font-semibold">Piano</th>
            <th className="px-4 py-3 font-semibold">Immobili</th>
            <th className="px-4 py-3 font-semibold">Borsellino</th>
            <th className="px-4 py-3 font-semibold text-right">Azione</th>
          </tr></thead>
          <tbody className="divide-y divide-border/60">
            {orgs === null ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Caricamento...</td></tr>
            ) : filtered.map((o) => (
              <tr key={o.id} data-testid={`impersonate-row-${o.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /> {o.name}</div>
                  <div className="text-xs text-muted-foreground ml-6">{o.contacts?.email}</div>
                </td>
                <td className="px-4 py-3 capitalize" data-testid={`impersonate-plan-${o.id}`}>{o.unlimited ? "Illimitato" : o.plan}{!o.unlimited && o.trial_active && <span className="text-xs text-amber-600 block">prova</span>}</td>
                <td className="px-4 py-3">{o.property_count}</td>
                <td className="px-4 py-3 font-mono-data">{eur(o.wallet_balance_cents)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => enter(o)} disabled={busy === o.id} className="bg-[#E3FF00] text-neutral-900 font-semibold px-3 py-2 rounded-lg hover:brightness-95 inline-flex items-center gap-1 disabled:opacity-60" data-testid={`impersonate-enter-${o.id}`}>
                    <LogIn className="h-4 w-4" /> Entra
                  </button>
                </td>
              </tr>
            ))}
            {orgs && filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Nessuna azienda trovata.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
